<script setup lang="ts">
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon, CaretUpIcon } from '@blpde/nx-icons';
import { computed, inject, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { splitterInjectionKey } from './internal';

const props = withDefaults(
  defineProps<{
    /** Whether the button down (used to expand the first panel) should be visible. Defaults to `false` */
    buttonDown?: boolean;
    /** Whether the button up (used to expand the second panel) should be visible. Defaults to `false` */
    buttonUp?: boolean;
    /** Accessible name of the divider, which the keyboard moves. */
    label?: string;
    /**
     * `'default'`: a line in a wide hit band that takes layout space between
     * the panels. `'overlay'`: takes no layout space — the line sits on the
     * panels' border with a narrow hit band laid over it (same hover, focus
     * and drag look), for app frames whose panes meet edge to edge.
     */
    variant?: 'default' | 'overlay';
    /**
     * Free-standing divider only (no surrounding `NxSplitterGroup`):
     * `'horizontal'` = a horizontal line moved up and down (default),
     * `'vertical'` = a vertical line moved left and right. Inside a group the
     * group's orientation applies. Setup-only.
     */
    orientation?: 'horizontal' | 'vertical';
    /** Free-standing only: the smallest `position` (px). Defaults to `0`. */
    min?: number;
    /** Free-standing only: the largest `position` (px). Defaults to the positioned parent's size. */
    max?: number;
  }>(),
  {
    buttonDown: false,
    buttonUp: false,
    label: 'Größe der Bereiche ändern',
    variant: 'default',
    orientation: 'horizontal',
    min: undefined,
    max: undefined,
  }
);

/**
 * Free-standing only: where the line sits, in px from the leading edge of the
 * positioned parent. A drag reports the pointer on every move and the keyboard
 * every step, kept inside `min` / `max` but otherwise raw — the owner decides
 * where the line goes (snapping it, storing it) and hands that back.
 */
const position = defineModel<number>('position', { default: 0 });

const emit = defineEmits<{
  /** Free-standing only: a pointer drag of the line started. */
  'drag-start': [];
  /** Free-standing only: the drag ended (pointer released or cancelled). */
  'drag-end': [];
}>();

const resizeHandle = useTemplateRef('splitter-resize-handle-hr');
const root = useTemplateRef('splitter-resize-handle');

/**
 * Without a surrounding `NxSplitterGroup` the handle is a free-standing,
 * controlled divider: its owner places it through `position` — for panes the
 * group cannot lay out, such as regions painted into one canvas.
 */
const splitterInjection = inject(splitterInjectionKey, null);
const isFree = splitterInjection === null;
const isVertical = splitterInjection
  ? splitterInjection.isVertical
  : props.orientation === 'vertical';
const isOverlay = isFree || props.variant === 'overlay';

/**
 * The divider line is the focusable separator (WAI-ARIA window splitter), not
 * the handle around it: a focusable separator's children are presentational,
 * and the collapse buttons next to the line must stay buttons.
 */
const separator = splitterInjection?.separator;

/** One arrow press moves the divider by a rem's worth of pixels, Shift by four. */
const KEYBOARD_STEP_PX = 16;

/** Free-standing: the parent's size along the axis, the default `max`. */
const parentSize = ref<number>();

function measureParent(): HTMLElement | null {
  const parent = root.value?.offsetParent;
  if (!(parent instanceof HTMLElement)) return null;
  parentSize.value = isVertical ? parent.clientWidth : parent.clientHeight;
  return parent;
}

function freeRange(): { lo: number; hi: number } {
  const lo = props.min ?? 0;
  const hi = props.max ?? parentSize.value ?? Number.POSITIVE_INFINITY;
  return { lo, hi: Math.max(lo, hi) };
}

function moveFreeTo(px: number) {
  const { lo, hi } = freeRange();
  position.value = Math.min(hi, Math.max(lo, px));
}

/** `aria-valuenow/min/max`: the group's percentages, or the free divider's pixels. */
const ariaValue = computed(() => {
  if (separator) return separator.value.value;
  if (!isFree) return undefined;
  return {
    now: Math.round(position.value),
    min: props.min ?? 0,
    max: props.max ?? parentSize.value,
  };
});

function onSeparatorKeydown(event: KeyboardEvent) {
  if (!separator && !isFree) return;
  // Only the arrows along the resize axis: a horizontal divider moves up/down.
  const [back, forward] = isVertical ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown'];
  const step = KEYBOARD_STEP_PX * (event.shiftKey ? 4 : 1);
  if (isFree) {
    measureParent();
    switch (event.key) {
      case back:
        moveFreeTo(position.value - step);
        break;
      case forward:
        moveFreeTo(position.value + step);
        break;
      case 'Home':
        moveFreeTo(freeRange().lo);
        break;
      case 'End':
        moveFreeTo(freeRange().hi);
        break;
      default:
        return;
    }
    // A free divider usually lies over an interactive surface (a grid) whose
    // own arrow keys must not run as well.
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  switch (event.key) {
    case back:
      separator!.resizeBy(-step);
      break;
    case forward:
      separator!.resizeBy(step);
      break;
    case 'Home':
      separator!.resizeToEdge('min');
      break;
    case 'End':
      separator!.resizeToEdge('max');
      break;
    default:
      return;
  }
  event.preventDefault();
}

/* ── Free-standing drag ──────────────────────────────────────────────── */

const draggingBodyClass = isVertical ? 'dragging-splitter-vertical' : 'dragging-splitter';
let dragParent: HTMLElement | null = null;

function pointerPosition(event: PointerEvent, parent: HTMLElement): number {
  const rect = parent.getBoundingClientRect();
  return isVertical
    ? event.clientX - rect.left - parent.clientLeft + parent.scrollLeft
    : event.clientY - rect.top - parent.clientTop + parent.scrollTop;
}

function onFreePointerMove(event: PointerEvent) {
  if (dragParent) moveFreeTo(pointerPosition(event, dragParent));
}

function stopFreeDrag(): boolean {
  if (!dragParent) return false;
  dragParent = null;
  window.removeEventListener('pointermove', onFreePointerMove);
  window.removeEventListener('pointerup', onFreePointerUp);
  window.removeEventListener('pointercancel', onFreePointerUp);
  document.body.classList.remove(draggingBodyClass);
  return true;
}

function onFreePointerUp() {
  if (stopFreeDrag()) emit('drag-end');
}

function onPointerDown(event: PointerEvent) {
  if (!isFree || event.button !== 0) return;
  const parent = measureParent();
  if (!parent) return;
  // The surface under the line (a grid) must not start a selection of its own.
  event.preventDefault();
  event.stopPropagation();
  // preventDefault also cancels the browser's focus move: focus the line
  // itself, or the keyboard never reaches it (a grid takes Tab for itself).
  resizeHandle.value?.focus({ preventScroll: true });
  stopFreeDrag();
  dragParent = parent;
  window.addEventListener('pointermove', onFreePointerMove, { passive: true });
  window.addEventListener('pointerup', onFreePointerUp);
  window.addEventListener('pointercancel', onFreePointerUp);
  document.body.classList.add(draggingBodyClass);
  emit('drag-start');
}

function onMouseDown(event: MouseEvent) {
  if (event.button === 0) splitterInjection?.startDragging(event);
}

/** Free-standing: the line placed at `position` inside the positioned parent. */
const freeStyle = computed(() => {
  if (!isFree) return undefined;
  return isVertical ? { left: `${position.value}px` } : { top: `${position.value}px` };
});

// On a vertical divider the "down" button expands the first (left) pane, so the
// caret points right (the direction the divider moves); "up" mirrors to left.
const buttonDownIcon = isVertical ? CaretRightIcon : CaretDownIcon;
const buttonUpIcon = isVertical ? CaretLeftIcon : CaretUpIcon;

onMounted(() => {
  if (isFree) {
    measureParent();
    return;
  }
  // Let the splitter group know the actual resize handle main-axis size (should be 38 if the
  // default font size is given). The overlay variant takes no layout space at all.
  const el = resizeHandle.value!;
  splitterInjection!.setResizeHandleHeight(
    isOverlay ? 0 : isVertical ? el.clientWidth : el.clientHeight
  );
});

// A handle removed mid-drag (its split cleared) still ends the owner's drag,
// while the owner's listeners are attached.
onBeforeUnmount(() => {
  if (stopFreeDrag()) emit('drag-end');
});
</script>

<template>
  <div
    ref="splitter-resize-handle"
    class="splitter-resize-handle"
    :class="{
      'splitter-resize-handle--vertical': isVertical,
      'splitter-resize-handle--overlay': isOverlay,
      'splitter-resize-handle--free': isFree,
    }"
    :style="freeStyle"
    @mousedown="onMouseDown"
    @pointerdown="onPointerDown"
  >
    <hr
      ref="splitter-resize-handle-hr"
      :tabindex="ariaValue ? 0 : undefined"
      :aria-label="ariaValue ? props.label : undefined"
      :aria-orientation="ariaValue ? (isVertical ? 'vertical' : 'horizontal') : undefined"
      :aria-valuenow="ariaValue?.now"
      :aria-valuemin="ariaValue?.min"
      :aria-valuemax="ariaValue?.max"
      :aria-controls="separator?.controls()"
      @keydown="onSeparatorKeydown"
    />
    <div
      v-if="!isFree && ($props.buttonUp || $props.buttonDown)"
      class="splitter-resize-handle-controls"
    >
      <div
        v-if="$props.buttonDown"
        v-show="splitterInjection!.showButtonDown.value"
        @mousedown.left.stop
      >
        <button @click="splitterInjection!.clickButtonDown">
          <div />
          <component :is="buttonDownIcon" filled class="nxt-icon-xl" color="var(--nxt-brand-bg)" />
          <div />
        </button>
      </div>
      <div
        v-if="$props.buttonUp"
        v-show="splitterInjection!.showButtonUp.value"
        @mousedown.left.stop
      >
        <button @click="splitterInjection!.clickButtonUp">
          <div />
          <component :is="buttonUpIcon" filled class="nxt-icon-xl" color="var(--nxt-brand-bg)" />
          <div />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A solid hairline in a wide hit band. Hover, focus and dragging thicken it
   and turn it brand; the collapse buttons are small raised chips on the line.
   `--splitter-line` / `--splitter-weight` are also set while dragging (the
   global rules below). */
.splitter-resize-handle {
  --splitter-line: var(--nxt-border);
  --splitter-weight: var(--nxt-hairline);

  position: relative;
  cursor: ns-resize;

  hr {
    height: 2.375em; /* 38px hit band */
    margin: 0;
    border: none;
    background-image: linear-gradient(var(--splitter-line), var(--splitter-line));
    background-size: 100% var(--splitter-weight);
    background-position: center;
    background-repeat: no-repeat;
    user-select: none;
    transition: background-size var(--nxt-motion-fast) var(--nxt-ease-out);
  }

  /* Grip in the middle of the line, only while it can be grabbed. */
  &:not(:has(.splitter-resize-handle-controls))::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(var(--nxt-size-unit) * 2);
    height: calc(var(--nxt-hairline) * 4);
    border-radius: var(--nxt-radius-full);
    background: var(--nxt-brand-bg);
    opacity: 0;
    transform: translate(-50%, -50%);
    pointer-events: none;
    transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
  }

  &:hover:not(:has(.splitter-resize-handle-controls *:hover)),
  &:has(hr:focus-visible) {
    --splitter-line: var(--nxt-brand-bg);
    --splitter-weight: calc(var(--nxt-hairline) * 2);

    &::after {
      opacity: 1;
    }
  }

  .splitter-resize-handle-controls {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 0);
    display: flex;
    gap: var(--nxt-space-xs);

    > div {
      display: flex;
      align-items: center;
      cursor: auto;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: calc(var(--nxt-size-unit) * 1.5);
      padding-inline: var(--nxt-space-sm);
      border: var(--nxt-hairline) solid var(--nxt-border);
      border-radius: var(--nxt-radius-full);
      background-color: var(--nxt-surface-bg);
      box-shadow: var(--nxt-shadow-sm);
      color: var(--nxt-brand-bg);
      cursor: pointer;
      transition:
        background-color var(--nxt-motion-fast) var(--nxt-ease-out),
        border-color var(--nxt-motion-fast) var(--nxt-ease-out);

      &:hover {
        border-color: var(--nxt-brand-bg);
        background-color: var(--nxt-brand-subtle-bg);
      }

      &:focus-visible {
        outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
        outline-offset: calc(var(--nxt-hairline) * 2);
      }

      /* The spacer dashes of the old pill design. */
      > div {
        display: none;
      }

      > svg {
        width: var(--nxt-icon-size-lg);
        height: var(--nxt-icon-size-lg);
      }
    }
  }
}

/* Keyboard focus on the divider line (the separator). */
.splitter-resize-handle hr:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

/* Vertical divider line (panes side by side): the same line rotated. */
.splitter-resize-handle--vertical {
  cursor: ew-resize;
}
.splitter-resize-handle--vertical hr {
  width: 2.375em; /* 38px — same hit band as the horizontal bar's height */
  /* Real height required: auto + inert align-self under a block parent painted 0px. */
  height: 100%;
  background-size: var(--splitter-weight) 100%;
}
.splitter-resize-handle--vertical:not(:has(.splitter-resize-handle-controls))::after {
  width: calc(var(--nxt-hairline) * 4);
  height: calc(var(--nxt-size-unit) * 2);
}

/* Collapse buttons on a vertical divider: stacked, centred on the line. */
.splitter-resize-handle--vertical .splitter-resize-handle-controls {
  top: 50%;
  bottom: auto;
  left: 0;
  right: 0;
  transform: translate(0, -50%);
  flex-direction: column;
}
.splitter-resize-handle--vertical .splitter-resize-handle-controls > div {
  justify-content: center;
}
.splitter-resize-handle--vertical .splitter-resize-handle-controls button {
  height: auto;
  width: calc(var(--nxt-size-unit) * 1.5);
  padding-inline: 0;
  padding-block: var(--nxt-space-sm);
}

/* Overlay variant: no layout space. The handle is a zero-size flex item on the
   border between the panels; the hit band is laid over both, centred on it. */
.splitter-resize-handle--overlay {
  --splitter-band: calc(var(--nxt-size-unit) * 0.75);

  flex: 0 0 0;
  z-index: var(--nxt-z-raised);
}
.splitter-resize-handle--overlay hr {
  position: absolute;
  top: calc(var(--splitter-band) / -2);
  left: 0;
  right: 0;
  height: var(--splitter-band);
}
.splitter-resize-handle--overlay.splitter-resize-handle--vertical hr {
  top: 0;
  bottom: 0;
  left: calc(var(--splitter-band) / -2);
  right: auto;
  width: var(--splitter-band);
  height: auto;
}

/* Free-standing divider: the overlay line placed absolutely in its positioned
   parent at `position` (inline `top` / `left`), across the parent's full
   width or height, above the content it divides. */
.splitter-resize-handle--free {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 0;
  z-index: var(--nxt-z-float);
  touch-action: none;
}
.splitter-resize-handle--free.splitter-resize-handle--vertical {
  bottom: 0;
  right: auto;
  width: 0;
  height: auto;
}

@media (prefers-reduced-motion: reduce) {
  .splitter-resize-handle hr,
  .splitter-resize-handle::after,
  .splitter-resize-handle .splitter-resize-handle-controls button {
    transition: none;
  }
}
</style>

<style>
/* While a divider is dragged — in a group or free-standing — the line stays in
   its hover look even when the pointer leaves the hit band. */
body.dragging-splitter {
  * {
    cursor: ns-resize !important;
  }
}
body.dragging-splitter-vertical {
  * {
    cursor: ew-resize !important;
  }
}
body.dragging-splitter .splitter-resize-handle,
body.dragging-splitter-vertical .splitter-resize-handle {
  --splitter-line: var(--nxt-brand-bg);
  --splitter-weight: calc(var(--nxt-hairline) * 2);
}
/* An iframe (a PDF preview beside the group) takes the pointer events over it,
   so a mouseup released there lands in the iframe's document, the window never
   sees it and the divider keeps following the mouse (I-2879). While a drag is
   in progress no iframe takes pointer events. */
body.dragging-splitter iframe,
body.dragging-splitter-vertical iframe {
  pointer-events: none;
}
</style>
